import React from 'react'

import { IS_EMBEDDED } from '@sketch/constants'
import { LoadingState } from '@sketch/components'
import { RenderStatus } from '@sketch/modules-common'

import { DocumentSidebarLayoutExtraProps } from '../../components/DocumentSidebarLayout'
import { useCanvasFocus } from '../../../canvas/scope'
import { CanvasProviders } from '../../../canvas/components/CanvasProviders'
import { useVersioning } from '../../../versioning'

import { LoadingMessage } from '../../components/LoadingMessage'
import ShareReadyGuard from '../../components/ShareReadyGuard'
import { CanvasSurface } from '../../components/CanvasSurface'
import { useCanvasPageUUID } from './useCanvasPageUUID'
import { ShareCanvasViews } from './ShareCanvasViews'
import { CanvasRouteComponentProps } from './types'

type ShareCanvasRouteProps = DocumentSidebarLayoutExtraProps &
  CanvasRouteComponentProps & {
    /**
     * The presentation file is still being written, on a route that cannot
     * draw without it. @see DocumentSidebarLayoutRoutes
     */
    isWaitingForPresentationFile?: boolean
    /**
     * How far along the document's render is. Passed in rather than queried
     * here; see `DocumentSidebarLayoutRoutes`.
     */
    renderStatus: RenderStatus
  }

/**
 * The page, Section and frame views of a Share, behind one route and one
 * canvas.
 *
 * They were three sibling routes rendering three identical provider stacks, so
 * moving between them unmounted one and mounted another - taking the renderer
 * with it, along with the WebGL context and the presentation file it had
 * already fetched. Everything those stacks needed turned out to differ by a
 * single value, the page being shown, so they collapse into this one and the
 * views below contribute only their own chrome.
 *
 * Which of the three renders is the same question `CanvasScope` answers for
 * everything beneath it, so it is asked once, the same way.
 */
export function ShareCanvasRoute({
  match,
  location,
  history,
  staticContext,
  isWaitingForPresentationFile,
  renderStatus,
  ...layoutProps
}: ShareCanvasRouteProps) {
  const focus = useCanvasFocus()
  const page = useCanvasPageUUID(focus)

  const { share, versionIdentifier, isViewingLatestVersion } = useVersioning()

  const routeProps: CanvasRouteComponentProps = {
    match,
    location,
    history,
    staticContext,
  }

  let content = null
  if (isWaitingForPresentationFile || page.status === 'loading') {
    content = (
      <LoadingState
        type="color-icon"
        size="48px"
        message={<LoadingMessage />}
      />
    )
  } else if (page.status === 'ready') {
    content = <CanvasSurface />
  } else {
    // No page, and none coming. Whatever the reason, the view below has
    // already said so, so nothing stands here - both at once is what an
    // embedded frame used to show.
    //
    // A frame that cannot be found is the case to know about: `FrameView`
    // redirects to the share on the error from the same query this route reads
    // (`FrameView.tsx`, the `error` branch), and names the missing frame
    // instead when embedded, where redirecting out of the iframe would be
    // worse than saying so.
    content = null
  }

  // Mounted even before the page is known. The frame route resolves its page
  // from the frame, so there is a beat where there is nothing to draw - but the
  // views below still render their headers and loading states, and everything
  // they use reads from these providers. Rendering them bare for that beat
  // throws. The canvas itself sits it out; see CanvasSurface.
  //
  // `IS_EMBEDDED` came from the page canvas, which was the only one of the
  // three to check it - the same gap the left panel had.
  return (
    <CanvasProviders
      pageUUID={page.status === 'ready' ? page.pageUUID : ''}
      focus={focus}
      shareIdentifier={share.identifier}
      versionIdentifier={versionIdentifier}
      annotationsDisabled={!share.commentsEnabled || IS_EMBEDDED}
      isViewingLatestVersion={isViewingLatestVersion}
    >
      {/* The views come first, as they did when each mounted its own canvas.
      They are almost entirely portals and floating controls, so this is not
      about layout - it is tab order. The page selector's menu is portalled to
      the body, so tabbing out of the selector reaches it only if the selector
      is late in the document; with the canvas first, the tab landed on the
      zoom control instead. */}
      <ShareCanvasViews
        focus={focus}
        layoutProps={layoutProps}
        routeProps={routeProps}
      />
      {/* One canvas for all three. The views above contribute their header,
      panels and left-panel navigation through portals, so moving between them
      changes what surrounds the canvas rather than replacing it.

      A document that is uploading, processing or has failed has nothing to
      draw, so the guard stands in for the canvas and the panel beside it - as
      it did in each view when each had its own canvas. It reads from the one
      place the canvas is mounted, which is also how the frame view comes to be
      covered by it for the first time. The views keep rendering their header
      and their left-panel portals either way: the portals find no host while
      the guard is up, and mount themselves when it comes down.

      The same slot covers not yet knowing which page to draw. A frame the
      canvas is already holding resolves to its page at once, but one on
      another page has to be looked up first, and until that answers there is
      nothing to draw - so it says so, rather than leaving the space blank.
      @see CanvasPageState

      Nested rather than beside it, so exactly one thing ever stands where the
      canvas goes. A document that is still being processed is also still
      missing its presentation file, and both used to say so at once - the
      guard's message with the file's spinner drawn over it. The guard's is the
      one worth reading; the spinner is for the short wait after a document is
      rendered but its file is not yet written. */}
      <ShareReadyGuard renderStatus={renderStatus}>{content}</ShareReadyGuard>
    </CanvasProviders>
  )
}
